import React, { useEffect, useState, useRef } from 'react';

import { usePersonalizationWidgetData } from '@vp/design-personalization-widget';
import { ChangeSource, OptionsChangedV1, PdpPageEvents } from '@vp/pdp-events';
import { useCustomLogger } from '@vp/pdp-ubik-logging';
import { useLocalizations } from '@vp/ubik-localizations-provider';

import { useAuthContext } from '../../../contexts/AuthProvider';
import { useFetchClient } from '../../../hooks/useFetchClient';
import { ICombination, ICurrentDocument, ProductMedias } from '../../../types/interfaces';
import { CarouselData } from '../domain/CarouselData';
import { Combination } from '../domain/Combination';

import { Carousel } from './Carousel';
import { buildHeroCarouselMedias } from './imageTransformations/buildHeroCarouselImages';

export interface PDPHeroCarouselProps {
  tenant: string;
  imagesTenant: string;
  merchant: string;
  accountId: string;
  selectedCombination: ICombination;
  mpvId: string;
  currentDocument?: ICurrentDocument;
  locale: string;
  productKey: string;
  productVersion: number;
  medias: ProductMedias;
  showBrandKit: boolean;
  onBrandkitApiError?: (hasError: boolean) => void;
  designPersonalizationContextIdOverride?: string;
  disableZoom?: boolean;
  productPageUrl?: string;
}

export const PDPHeroCarousel: React.FC<PDPHeroCarouselProps> = (props) => {
  const { t } = useLocalizations();
  const { logger } = useCustomLogger();
  const { isInitialized, authorizationHeader } = useAuthContext();
  const fetchClient = useFetchClient();

  const [heroCarouselData, setHeroCarouselData] = useState(
    CarouselData.from(buildHeroCarouselMedias(props.medias))
  );
  const [combination, setCombination] = useState<Combination>(
    new Combination(props.selectedCombination, true)
  );
  const [currentDocument, setCurrentDocument] = useState<ICurrentDocument | undefined>(
    props.currentDocument
      ? {
          documentUri: props.currentDocument.documentUri,
          altText: t('shop-with-your-design-alt-text'),
        }
      : undefined
  );
  const [isOverrideInitialized, setIsOverrideInitialized] = useState(false);

  const {
    designPersonalizationContextId,
    isPersonalizationWidgetUpdating,
    designPersonalizationError,
    setDesignPersonalizationContextEnabled,
  } = props.showBrandKit
    ? usePersonalizationWidgetData()
    : {
        designPersonalizationContextId: undefined,
        isPersonalizationWidgetUpdating: false,
        designPersonalizationError: null,
        setDesignPersonalizationContextEnabled: () => {},
      };

  useEffect(() => {
    if (props.designPersonalizationContextIdOverride && !isOverrideInitialized) {
      if (!designPersonalizationContextId) {
        setDesignPersonalizationContextEnabled(true);
      }
      if (designPersonalizationContextId) {
        setIsOverrideInitialized(true);
        if (props.designPersonalizationContextIdOverride !== designPersonalizationContextId) {
          setDesignPersonalizationContextEnabled(false);
        }
      }
    }
  }, [
    props.designPersonalizationContextIdOverride,
    designPersonalizationContextId,
    isOverrideInitialized,
    setDesignPersonalizationContextEnabled,
  ]);

  const shouldUseBrandkit = props.showBrandKit && !designPersonalizationError;

  const combinationRef = useRef(combination);
  const currentDocumentRef = useRef(currentDocument);
  const designPersonalizationContextIdRef = useRef(designPersonalizationContextId);
  const shouldUseBrandkitRef = useRef(shouldUseBrandkit);
  const documentLoadAbortControllerRef = useRef<AbortController | null>(null);

  const updateBrandkitPreview = async (
    combination: Combination,
    designPersonalizationContextId: string | undefined,
    signal?: AbortSignal
  ) => {
    if (signal?.aborted) return;

    const isRemoved = heroCarouselData.removeBrandkitImage();
    if (designPersonalizationContextId) {
      const result = await heroCarouselData.addBrandkitImage(
        props.locale,
        props.productKey,
        props.productVersion,
        designPersonalizationContextId!,
        combination,
        t('brandkit-image-alt-text'),
        fetchClient,
        logger
      );

      if (signal?.aborted) return;
      if (result) {
        setHeroCarouselData(heroCarouselData.copy());
        props.onBrandkitApiError?.(false);
        PdpPageEvents.sendDesignPersonalizationContextIdUpdatedEventV1({
          designPersonalizationContextId: designPersonalizationContextId,
        });
      } else {
        PdpPageEvents.sendDesignPersonalizationContextIdUpdatedEventV1({
          designPersonalizationContextId: null,
        });
        props.onBrandkitApiError?.(true);
      }
    } else {
      if (isRemoved) {
        setHeroCarouselData(heroCarouselData.copy());
        PdpPageEvents.sendDesignPersonalizationContextIdUpdatedEventV1({
          designPersonalizationContextId: null,
        });
      }
    }
  };

  useEffect(() => {
    if (!isPersonalizationWidgetUpdating && shouldUseBrandkit) {
      updateBrandkitPreview(combination, designPersonalizationContextId);
    }
  }, [designPersonalizationContextId, isPersonalizationWidgetUpdating, shouldUseBrandkit]);

  const calculateDocumentImages = async (
    currentDocument: ICurrentDocument | undefined,
    combination: Combination,
    signal?: AbortSignal
  ) => {
    if (!currentDocument) return;
    if (!isInitialized) return;
    if (signal?.aborted) return;

    const result = await heroCarouselData.addDocumentImage(
      currentDocument!,
      props.imagesTenant,
      props.merchant,
      props.accountId,
      props.locale,
      props.productKey,
      props.productVersion,
      combination,
      logger,
      { Authorization: authorizationHeader! },
      fetchClient,
      signal
    );

    if (signal?.aborted) return;
    if (!result) {
      logger.error('Failed to transform template into document', {
        productKey: props.productKey,
        productVersion: props.productVersion,
        documentUri: currentDocument.documentUri,
      });
      setCurrentDocument(undefined);
      PdpPageEvents.sendCurrentDocumentInvalidatedEventV1({
        reason: 'ErrorGeneratingScenes',
      });
    } else {
      setHeroCarouselData(heroCarouselData.copy());
    }
  };

  const resetDocumentLoadAbortController = (): AbortController => {
    documentLoadAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    documentLoadAbortControllerRef.current = abortController;
    return abortController;
  };

  useEffect(() => {
    combinationRef.current = combination;
  }, [combination]);

  useEffect(() => {
    currentDocumentRef.current = currentDocument;
  }, [currentDocument]);

  useEffect(() => {
    designPersonalizationContextIdRef.current = designPersonalizationContextId;
  }, [designPersonalizationContextId]);

  useEffect(() => {
    shouldUseBrandkitRef.current = shouldUseBrandkit;
  }, [shouldUseBrandkit]);

  useEffect(() => {
    const handleOptionsChanged = async (optionsChanged: OptionsChangedV1) => {
      const abortController = resetDocumentLoadAbortController();

      const prevCurrentDocument = currentDocumentRef.current;

      const designPersonalizationContextId = designPersonalizationContextIdRef.current;
      const newCombination = new Combination(
        optionsChanged.combination,
        optionsChanged.source !== ChangeSource.USER
      );
      await calculateDocumentImages(prevCurrentDocument, newCombination, abortController.signal);

      if (designPersonalizationContextId && shouldUseBrandkitRef.current)
        await updateBrandkitPreview(
          newCombination,
          designPersonalizationContextId,
          abortController.signal
        );

      setCombination((prevCombination) => {
        return newCombination.equals(prevCombination) ? prevCombination : newCombination;
      });
    };
    const unsubscribeOrVoid = PdpPageEvents.listenToOptionsChangedEventV1(handleOptionsChanged);

    return () => {
      documentLoadAbortControllerRef.current?.abort();
      if (typeof unsubscribeOrVoid === 'function') {
        unsubscribeOrVoid();
      }
    };
  }, [isInitialized]);

  useEffect(() => {
    const handleDocumentChange = async () => {
      if (
        currentDocument &&
        currentDocument.documentUri &&
        props.productKey &&
        props.productVersion
      ) {
        const abortController = resetDocumentLoadAbortController();
        await calculateDocumentImages(currentDocument, combination, abortController.signal);
      } else {
        const isRemoved = heroCarouselData.removeDocumentImage();
        if (isRemoved) setHeroCarouselData(heroCarouselData.copy());
      }
    };
    handleDocumentChange();
  }, [currentDocument, isInitialized]);

  return (
    <Carousel
      tenant={props.tenant}
      carouselData={heroCarouselData}
      selectedCombination={combination ?? heroCarouselData.getFirstCombination()}
      mpvId={props.mpvId}
      productKey={props.productKey}
      disableZoom={props.disableZoom}
      productPageUrl={props.productPageUrl}
    />
  );
};
